import Layout from "../../../components/layout/Layout";
import * as P from "../../../components/customizing/print/Print.styles";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import PrintInquiryModal from "../../../components/customizing/print/PrintInquiryModal";
import { failAlert, successAlert } from "../../../common/alert";
import { connect } from "react-redux";
import { inquirySet } from "../../../api/products";
import InquiryCompleteModal from "../../../components/elements/inquiryCompleteModal";
import { isLoaded, isLoading } from "../../../redux/action/loading";
import PrintPageUI from "../../../components/customizing/print/Print.presenter";
import PrintProductsList from "../../../components/customizing/print/Print.ProductsList";
import { smallPackagingAPI } from "../../../api/smallPackaging";
import PrintCart from "../../../components/customizing/print/printCart/PrintCart";
import { getUserInfo } from "../../../api/getUserInfo";
import {
  closeConfirmModal,
  modifyConfirmModal,
  openConfirmModal,
} from "../../../redux/action/confirm";
import FilterList from "../../../components/filterList/FilterList";
import { useRouter } from "next/router";
import Head from "next/head";
import { SeoMeta } from "@/utils/seo/SeoMeta";

const CustomizingPrintPage = ({
  isLoaded,
  isLoading,
  closeConfirmModal,
  modifyConfirmModal,
  openConfirmModal,
  userInfo,
}) => {
  const router = useRouter();
  const [inquiryInputs, setInquiryInputs] = useState({});
  const [openInquiryModal, setOpenInquiryModal] = useState(false);
  const [openInquiryComplete, setOpenInquiryComplete] = useState(false);
  const [productList, setProductList] = useState([]);
  const [printCartItems, setPrintCartItems] = useState([]);
  const cartRef = useRef(null);
  const listRef = useRef(null);

  const userCompany = useMemo(() => {
    if (userInfo?.Company?.Data?.code) {
      return userInfo?.Company?.Data;
    } else {
      return null;
    }
  }, [userInfo]);

  const onClickInquiry = async () => {
    setOpenInquiryModal((prev) => !prev);
    setInquiryInputs({});
  };

  const onChangeInquiry = (e, id) => {
    if (id.includes("file")) {
      setInquiryInputs({ ...inquiryInputs, [id]: e.target.files[0] });
      return;
    }
    if (id === "policy") {
      setInquiryInputs({ ...inquiryInputs, [id]: e.target.checked });
      return;
    }
    setInquiryInputs({ ...inquiryInputs, [id]: e.target.value });
  };

  const onClickInquiryComplete = () => {
    setOpenInquiryComplete((prev) => !prev);
  };

  const submitInquiry = async () => {
    if (
      !inquiryInputs.name ||
      !inquiryInputs.tel ||
      !inquiryInputs.cnt ||
      !inquiryInputs.usages ||
      !inquiryInputs.email ||
      !inquiryInputs.content
    ) {
      failAlert("항목을 전부 입력해주세요.");
      return;
    }
    if (!inquiryInputs.policy) {
      failAlert("유의사항에 동의해주세요.");
      return;
    }
    if (
      !inquiryInputs.tel.match(
        /^(0[2-8][0-5]?|01[01346-9])-?([1-9]{1}[0-9]{2,3})-?([0-9]{4})$/
      )
    ) {
      failAlert("사용가능한 전화번호를 입력해주세요.");
      return;
    }
    isLoading();
    const body = {
      modes: "InquirySet",
      division: "F",
      pcode: "P000001",
      ...inquiryInputs,
    };
    const res = await inquirySet(body);
    if (res.Code === "0000") {
      successAlert(res.Data?.Msg || "문의가 등록되었습니다.");
      onClickInquiryComplete();
      onClickInquiry();
      setInquiryInputs({});
      isLoaded();
    } else {
      failAlert("문의 등록에 실패했습니다.");
      isLoaded();
    }
  };

  // 소량 인쇄 상품 리스트
  const fetchProducts = async () => {
    const body = {
      modes: "ProductsListSet",
    };
    const res = await smallPackagingAPI(body);

    if (res.Code === "0000") {
      setProductList(res.Data?.rows.data);
    }
  };

  // 견적 카트 상품 불러오기
  const fetchPrintCartItems = () => {
    const items = JSON.parse(sessionStorage.getItem("printCart"));
    if (items && items.length > 0) {
      setPrintCartItems(items);
    }
  };

  // 카트 담기
  const addPrintCart = (product) => {
    if (!userInfo?.Member?.ukey) {
      failAlert("로그인 후 이용 가능합니다.");
      router.push("/login");
      return;
    }
    if (printCartItems.find((el) => el.pcode === product.pcode)) {
      failAlert("이미 담긴 상품입니다.");
      return;
    }
    successAlert("장바구니에 담겼습니다.");
    const tempArr = [{ ...product, cnt: 1 }, ...printCartItems];
    setPrintCartItems(tempArr);
    sessionStorage.setItem("printCart", JSON.stringify(tempArr));
    window.scrollTo({
      behavior: "smooth",
      left: 0,
      top: cartRef.current.offsetTop,
    });
  };

  // 카트 담긴 상태
  const inCart = (product) => {
    if (!userInfo?.Member?.ukey) {
      return false;
    }
    return printCartItems.find((el) => el.pcode === product.pcode)
      ? true
      : false;
  };

  useEffect(() => {
    fetchProducts();
    fetchPrintCartItems();
  }, []);

  return (
    <>
      <Head>
        <meta property="og:title" content={"소량 인쇄 서비스 :: 칼렛스토어"} />
        <meta
          property="og:image"
          content="https://file.caretbio.com/caretbio/b2c6985b05f7bef24ed2c91965b47455.jpg"
        />
        <meta
          property="og:url"
          content={"https://www.caretstore.co.kr/customizing/print/"}
        />
      </Head>
      <Layout
        parent={"홈"}
        sub={"소량 인쇄 서비스"}
        pageTitle={SeoMeta.smallPrintService.title}
        pageDesc={SeoMeta.smallPrintService.description}
        pageKeyword={SeoMeta.smallPrintService.keywords}
      >
        <P.PrintSection>
          <PrintPageUI
            onClickInquiry={onClickInquiry}
            listRef={listRef}
            cartRef={cartRef}
            productList={productList}
            addPrintCart={addPrintCart}
            inCart={inCart}
            userInfo={userInfo}
            userCompany={userCompany}
            printCartItems={printCartItems}
            setPrintCartItems={setPrintCartItems}
          />
        </P.PrintSection>
      </Layout>
      {openInquiryModal && (
        <PrintInquiryModal
          onClickInquiry={onClickInquiry}
          inquiryInputs={inquiryInputs}
          onChangeInquiry={onChangeInquiry}
          submitInquiry={submitInquiry}
        />
      )}
      {openInquiryComplete && (
        <InquiryCompleteModal onClickInquiryComplete={onClickInquiryComplete} />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
});

const mapDispatchToProps = {
  isLoading,
  isLoaded,
  closeConfirmModal,
  modifyConfirmModal,
  openConfirmModal,
};
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CustomizingPrintPage);
